// src/evaluations/EvaluationTalentMapLayout.js
import React, { useMemo } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    Avatar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    ReferenceArea,
    Cell
} from 'recharts';
import { useTranslation } from 'react-i18next';

// Componente estilizado para las cards
const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[8],
    },
}));

// Componente para las cards de leyenda
const LegendCard = styled(Card)(({ theme, color }) => ({
    border: `2px solid ${color}`,
    backgroundColor: `${color}15`,
    '&:hover': {
        backgroundColor: `${color}25`,
        transform: 'translateY(-1px)',
        boxShadow: theme.shadows[4],
    },
    transition: 'all 0.2s ease',
}));

const EvaluationTalentMapLayout = ({ candidatesData, evaluationResponses }) => {
    const { t } = useTranslation();

    // Procesar datos para el scatter chart
    const chartData = useMemo(() => {
        return evaluationResponses
            .filter(response => response.overallStatus === 'completed')
            .map(response => ({
                name: response.applicantName,
                desempeño: response.overallScore || 0,
                perfil: response.reportOverallScore || 0,
                id: response.id,
                applicantId: response.applicantId
            }));
    }, [evaluationResponses]);

    // Función para determinar el color según la zona (COLORES EXACTOS ACTUALIZADOS)
    const getZoneColor = (desempeño, perfil) => {
        // 1. Rendimiento bajo: perfil <=33 Y desempeño <=33
        if (perfil <= 33 && desempeño <= 33) return '#de3722';
        
        // 2. Rendimiento inconsistente: perfil <=33 Y desempeño >33 & <=66
        if (perfil <= 33 && desempeño > 33 && desempeño <= 66) return '#e3706a';
        
        // 3. Diamante en bruto: perfil <=33 Y desempeño >66
        if (perfil <= 33 && desempeño > 66) return '#f3da79';
        
        // 4. Profesional sólido: perfil >33 & <=66 Y desempeño <=33
        if (perfil > 33 && perfil <= 66 && desempeño <= 33) return '#e3706a';
        
        // 5. Profesional clave de alto rendimiento: perfil >33 & <=66 Y desempeño >33 & <=66
        if (perfil > 33 && perfil <= 66 && desempeño > 33 && desempeño <= 66) return '#eec245';
        
        // 6. Futura estrella: perfil >33 & <=66 Y desempeño >66
        if (perfil > 33 && perfil <= 66 && desempeño > 66) return '#b1ce94';
        
        // 7. Profesional altamente calificado: perfil >66 Y desempeño <=33
        if (perfil > 66 && desempeño <= 33) return '#f3da79';
        
        // 8. Profesional más que altamente calificado: perfil >66 Y desempeño >33 & <=66
        if (perfil > 66 && desempeño > 33 && desempeño <= 66) return '#b1ce94';
        
        // 9. Estrella consistente: perfil >66 Y desempeño >66
        if (perfil > 66 && desempeño > 66) return '#80ab56';
        
        return '#e0e0e0'; // Color por defecto
    };

    // Función para obtener la clasificación (CONDICIONES EXACTAS)
    const getClassification = (desempeño, perfil) => {
        // 1. Rendimiento bajo: perfil <=33 Y desempeño <=33
        if (perfil <= 33 && desempeño <= 33) return t('evaluationResponses.talentMap.underPerformer');
        
        // 2. Rendimiento inconsistente: perfil <=33 Y desempeño >33 & <=66
        if (perfil <= 33 && desempeño > 33 && desempeño <= 66) return t('evaluationResponses.talentMap.developmentNeeded');
        
        // 3. Diamante en bruto: perfil <=33 Y desempeño >66
        if (perfil <= 33 && desempeño > 66) return t('evaluationResponses.talentMap.inconsistentPerformer');
        
        // 4. Profesional sólido: perfil >33 & <=66 Y desempeño <=33
        if (perfil > 33 && perfil <= 66 && desempeño <= 33) return t('evaluationResponses.talentMap.coreProfessional');
        
        // 5. Profesional clave de alto rendimiento: perfil >33 & <=66 Y desempeño >33 & <=66
        if (perfil > 33 && perfil <= 66 && desempeño > 33 && desempeño <= 66) return t('evaluationResponses.talentMap.keyContributor');
        
        // 6. Futura estrella: perfil >33 & <=66 Y desempeño >66
        if (perfil > 33 && perfil <= 66 && desempeño > 66) return t('evaluationResponses.talentMap.emergingTalent');
        
        // 7. Profesional altamente calificado: perfil >66 Y desempeño <=33
        if (perfil > 66 && desempeño <= 33) return t('evaluationResponses.talentMap.highPotential');
        
        // 8. Profesional más que altamente calificado: perfil >66 Y desempeño >33 & <=66
        if (perfil > 66 && desempeño > 33 && desempeño <= 66) return t('evaluationResponses.talentMap.highPerformer');
        
        // 9. Estrella consistente: perfil >66 Y desempeño >66
        if (perfil > 66 && desempeño > 66) return t('evaluationResponses.talentMap.starPerformer');
        
        return t('evaluationResponses.talentMap.unclassified');
    };

    // Datos para las estadísticas
    const stats = useMemo(() => {
        const total = chartData.length;
        const highPerformers = chartData.filter(c => c.desempeño >= 66 && c.perfil >= 66).length;
        const needsDevelopment = chartData.filter(c => c.desempeño < 33 || c.perfil < 33).length;
        const averagePerformance = total > 0 ? chartData.reduce((sum, c) => sum + c.desempeño, 0) / total : 0;
        const averageProfile = total > 0 ? chartData.reduce((sum, c) => sum + c.perfil, 0) / total : 0;

        return { total, highPerformers, needsDevelopment, averagePerformance, averageProfile };
    }, [chartData]);

    // Custom tooltip
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <Paper sx={{ p: 2, maxWidth: 250 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                        {data.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('evaluationResponses.talentMap.performance')}: {data.desempeño}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('evaluationResponses.talentMap.profile')}: {data.perfil}%
                    </Typography>
                    <Chip
                        label={getClassification(data.desempeño, data.perfil)}
                        size="small"
                        sx={{
                            mt: 1,
                            backgroundColor: getZoneColor(data.desempeño, data.perfil) + '20',
                            color: getZoneColor(data.desempeño, data.perfil),
                            fontWeight: 'bold'
                        }}
                    />
                </Paper>
            );
        }
        return null;
    };

    if (chartData.length === 0) {
        return (
            <Alert severity="info" sx={{ mt: 2 }}>
                {t('evaluationResponses.talentMap.noDataAvailable')}
            </Alert>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
                    {t('evaluationResponses.talentMap.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {t('evaluationResponses.talentMap.description')}
                </Typography>
            </Box>

            {/* Estadísticas generales */}
            {/* <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StyledCard>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight="bold" color="primary">
                                {stats.total}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('evaluationResponses.talentMap.totalCandidates')}
                            </Typography>
                        </CardContent>
                    </StyledCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StyledCard>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight="bold" color="success.main">
                                {stats.highPerformers}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('evaluationResponses.talentMap.highPerformers')}
                            </Typography>
                        </CardContent>
                    </StyledCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StyledCard>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight="bold" color="warning.main">
                                {stats.needsDevelopment}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('evaluationResponses.talentMap.needsDevelopment')}
                            </Typography>
                        </CardContent>
                    </StyledCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StyledCard>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" fontWeight="bold" color="info.main">
                                {stats.averagePerformance.toFixed(1)}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('evaluationResponses.talentMap.averagePerformance')}
                            </Typography>
                        </CardContent>
                    </StyledCard>
                </Grid>
            </Grid> */}

            <Grid container spacing={3}>
                {/* Gráfico principal CON CUADRANTES DE FONDO */}
                <Grid item xs={12} lg={8}>
                    <StyledCard>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t('evaluationResponses.talentMap.matrixTitle')}
                            </Typography>
                            
                            <Box sx={{ height: 500, width: '100%' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
                                        {/* Cuadrantes de fondo - COLORES ACTUALIZADOS EXACTOS */}
                                        
                                        {/* FILA SUPERIOR (Perfil Alto >66) */}
                                        <ReferenceArea x1={0} x2={33} y1={66} y2={100} fill="#f3da79" fillOpacity={0.3} /> {/* Profesional Altamente Calificado */}
                                        <ReferenceArea x1={33} x2={66} y1={66} y2={100} fill="#b1ce94" fillOpacity={0.3} /> {/* Prof. más que Alt. Calificado */}
                                        <ReferenceArea x1={66} x2={100} y1={66} y2={100} fill="#80ab56" fillOpacity={0.3} /> {/* Estrella Consistente */}
                                        
                                        {/* FILA MEDIA (Perfil Medio >33 & <=66) */}
                                        <ReferenceArea x1={0} x2={33} y1={33} y2={66} fill="#e3706a" fillOpacity={0.3} /> {/* Profesional Sólido */}
                                        <ReferenceArea x1={33} x2={66} y1={33} y2={66} fill="#eec245" fillOpacity={0.3} /> {/* Profesional Clave de Alto Rendimiento */}
                                        <ReferenceArea x1={66} x2={100} y1={33} y2={66} fill="#b1ce94" fillOpacity={0.3} /> {/* Futura Estrella */}
                                        
                                        {/* FILA INFERIOR (Perfil Bajo <=33) */}
                                        <ReferenceArea x1={0} x2={33} y1={0} y2={33} fill="#de3722" fillOpacity={0.3} /> {/* Rendimiento Bajo */}
                                        <ReferenceArea x1={33} x2={66} y1={0} y2={33} fill="#e3706a" fillOpacity={0.3} /> {/* Rendimiento Inconsistente */}
                                        <ReferenceArea x1={66} x2={100} y1={0} y2={33} fill="#f3da79" fillOpacity={0.3} /> {/* Diamante en Bruto */}
                                        
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                        
                                        <XAxis
                                            type="number"
                                            dataKey="desempeño"
                                            name={t('evaluationResponses.talentMap.performance')}
                                            domain={[0, 100]}
                                            label={{
                                                value: t('evaluationResponses.talentMap.performanceAxis'),
                                                position: 'insideBottom',
                                                offset: -10,
                                                style: { textAnchor: 'middle' }
                                            }}
                                            tickFormatter={(value) => `${value}%`}
                                        />
                                        <YAxis
                                            type="number"
                                            dataKey="perfil"
                                            name={t('evaluationResponses.talentMap.profile')}
                                            domain={[0, 100]}
                                            label={{
                                                value: t('evaluationResponses.talentMap.profileAxis'),
                                                angle: -90,
                                                position: 'insideLeft',
                                                style: { textAnchor: 'middle' }
                                            }}
                                            tickFormatter={(value) => `${value}%`}
                                        />
                                        
                                        {/* Líneas de referencia - NEGRAS Y SÓLIDAS */}
                                        <ReferenceLine x={33} stroke="#000000" strokeWidth={1} />
                                        <ReferenceLine x={66} stroke="#000000" strokeWidth={1} />
                                        <ReferenceLine y={33} stroke="#000000" strokeWidth={1} />
                                        <ReferenceLine y={66} stroke="#000000" strokeWidth={1} />
                                        
                                        <Tooltip content={<CustomTooltip />} />
                                        
                                        <Scatter name={t('evaluationResponses.talentMap.candidates')} data={chartData} fill="#8884d8">
                                            {chartData.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={getZoneColor(entry.desempeño, entry.perfil)}
                                                    stroke="#fff"
                                                    strokeWidth={2}
                                                />
                                            ))}
                                        </Scatter>
                                    </ScatterChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </StyledCard>
                </Grid>

                {/* Leyenda expandida con LAS 9 CLASIFICACIONES */}
                <Grid item xs={12} lg={4}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="h6" gutterBottom>
                            {t('evaluationResponses.talentMap.legend')}
                        </Typography>
                        
                        {/* Zona 1: Estrella Consistente */}
                        <LegendCard color="#80ab56">
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#4A6741', fontSize: '0.75rem' }}>
                                    1. {t('evaluationResponses.talentMap.starPerformer')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {t('evaluationResponses.talentMap.starPerformerDesc')}
                                </Typography>
                            </CardContent>
                        </LegendCard>

                        {/* Zona 2: Profesional más que Altamente Calificado */}
                        <LegendCard color="#b1ce94">
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#6B8E23', fontSize: '0.75rem' }}>
                                    2. {t('evaluationResponses.talentMap.highPerformer')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {t('evaluationResponses.talentMap.highPerformerDesc')}
                                </Typography>
                            </CardContent>
                        </LegendCard>

                        {/* Zona 3: Profesional Altamente Calificado */}
                        <LegendCard color="#f3da79">
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#D2691E', fontSize: '0.75rem' }}>
                                    3. {t('evaluationResponses.talentMap.highPotential')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {t('evaluationResponses.talentMap.highPotentialDesc')}
                                </Typography>
                            </CardContent>
                        </LegendCard>

                        {/* Zona 4: Futura Estrella */}
                        <LegendCard color="#b1ce94">
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#6B8E23', fontSize: '0.75rem' }}>
                                    4. {t('evaluationResponses.talentMap.emergingTalent')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {t('evaluationResponses.talentMap.emergingTalentDesc')}
                                </Typography>
                            </CardContent>
                        </LegendCard>

                        {/* Zona 5: Profesional Clave de Alto Rendimiento */}
                        <LegendCard color="#eec245">
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#B8860B', fontSize: '0.75rem' }}>
                                    5. {t('evaluationResponses.talentMap.keyContributor')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {t('evaluationResponses.talentMap.keyContributorDesc')}
                                </Typography>
                            </CardContent>
                        </LegendCard>

                        {/* Zona 6: Profesional Sólido */}
                        <LegendCard color="#e3706a">
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#B71C1C', fontSize: '0.75rem' }}>
                                    6. {t('evaluationResponses.talentMap.coreProfessional')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {t('evaluationResponses.talentMap.coreProfessionalDesc')}
                                </Typography>
                            </CardContent>
                        </LegendCard>

                        {/* Zona 7: Un Diamante en Bruto */}
                        <LegendCard color="#f3da79">
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#D2691E', fontSize: '0.75rem' }}>
                                    7. {t('evaluationResponses.talentMap.inconsistentPerformer')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {t('evaluationResponses.talentMap.inconsistentPerformerDesc')}
                                </Typography>
                            </CardContent>
                        </LegendCard>

                        {/* Zona 8: Trabajador de Rendimiento Inconsistente */}
                        <LegendCard color="#e3706a">
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#B71C1C', fontSize: '0.75rem' }}>
                                    8. {t('evaluationResponses.talentMap.developmentNeeded')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {t('evaluationResponses.talentMap.developmentNeededDesc')}
                                </Typography>
                            </CardContent>
                        </LegendCard>

                        {/* Zona 9: Trabajador de Rendimiento Bajo */}
                        <LegendCard color="#de3722">
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#8B0000', fontSize: '0.75rem' }}>
                                    9. {t('evaluationResponses.talentMap.underPerformer')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {t('evaluationResponses.talentMap.underPerformerDesc')}
                                </Typography>
                            </CardContent>
                        </LegendCard>
                    </Box>
                </Grid>
            </Grid>

            {/* Tabla de resumen */}
            <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                    {t('evaluationResponses.talentMap.candidatesSummary')}
                </Typography>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'grey.50' }}>
                                <TableCell>{t('evaluationResponses.talentMap.candidate')}</TableCell>
                                <TableCell align="center">{t('evaluationResponses.talentMap.performance')}</TableCell>
                                <TableCell align="center">{t('evaluationResponses.talentMap.profile')}</TableCell>
                                <TableCell>{t('evaluationResponses.talentMap.classification')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {chartData
                                .sort((a, b) => (b.desempeño + b.perfil) - (a.desempeño + a.perfil))
                                .map((candidate, index) => (
                                <TableRow key={index} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                                                {candidate.name.charAt(0).toUpperCase()}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight="medium">
                                                {candidate.name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography variant="body2" fontWeight="bold">
                                            {candidate.desempeño}%
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography variant="body2" fontWeight="bold">
                                            {candidate.perfil}%
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getClassification(candidate.desempeño, candidate.perfil)}
                                            size="small"
                                            sx={{
                                                backgroundColor: getZoneColor(candidate.desempeño, candidate.perfil) + '20',
                                                color: getZoneColor(candidate.desempeño, candidate.perfil),
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

export default EvaluationTalentMapLayout;